import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCreateBuildingsForTenantMutation } from '@client/api';
import type {
  BulkBuildingPayload,
  BulkChildBuildingPayload,
  BulkBuildingPackagePayload,
  BulkImportCompanyPayload,
} from '@client/api';

import { BuildingWizardLayout } from './building-wizard-layout';
import {
  BuildingWizardState,
  STEP_BYGG,
  STEP_FERDIG,
  STEP_GJENNOMGANG,
  STEP_IMPORTER,
  STEP_PAKKER,
  STEP_ROLLER,
} from './building-wizard.types';
import { StepBygg } from './step-bygg';
import { StepRoller } from './step-roller';
import { StepImporter } from './step-importer';
import { StepPakker } from './step-pakker';
import { StepGjennomgang } from './step-gjennomgang';
import { StepFerdig } from './step-ferdig';

import type { OnboardingBuilding, ImportCompanyCandidate } from '../../onboarding/onboarding.types';
import { PortfolioPaths } from '../../portfolio.router';

const STORAGE_KEY = 'exio.buildingWizard';

const INITIAL_STATE: BuildingWizardState = {
  currentStep: STEP_BYGG,
  buildings: [],
  importCompanies: [],
  packagesByBuildingId: {},
};

/**
 * Clears the persisted wizard state so the next mount starts from step 1.
 * Entry points (e.g. the "Legg til bygning" button) call this before navigating,
 * while a hard refresh mid-flow still restores the saved state.
 */
export function resetBuildingWizardState() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable — ignore */
  }
}

function loadSavedState(): BuildingWizardState {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as BuildingWizardState;
      // Don't restore at the gjennomgang step or beyond — submit must be deliberate.
      if (parsed.currentStep >= STEP_GJENNOMGANG) return INITIAL_STATE;
      return {
        ...INITIAL_STATE,
        ...parsed,
        importCompanies: parsed.importCompanies ?? [],
        packagesByBuildingId: parsed.packagesByBuildingId ?? {},
      };
    }
  } catch {
    // corrupt data — start fresh
  }
  return INITIAL_STATE;
}

/**
 * Multi-step "Create building" wizard for users already inside a tenant.
 *
 * Reuses the StepBygg / StepRoller / StepImporter components from the portfolio
 * onboarding flow (now living in this folder), and adds an in-tenant-specific
 * StepPakker (per-building package picker), StepGjennomgang (review), and
 * StepFerdig (success). Submits via <c>POST /buildings/bulk</c>.
 *
 * State persists to localStorage so a hard refresh mid-flow doesn't wipe input,
 * but is cleared on successful submit and never restores into the submit step.
 */
export function BuildingWizard() {
  const [state, setState] = useState<BuildingWizardState>(loadSavedState);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [createdBuildingIds, setCreatedBuildingIds] = useState<string[]>([]);

  const navigate = useNavigate();
  const [createBuildings, { isLoading: isSubmitting }] = useCreateBuildingsForTenantMutation();

  // Persist state on every change.
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* storage full / unavailable — ignore */
    }
  }, [state]);

  // Seed default Portfolio:Basic packages for every newly-selected building so the
  // pakker step has a valid baseline (matches portfolio onboarding's hardcoded default).
  useEffect(() => {
    setState((prev) => {
      const next = { ...prev.packagesByBuildingId };
      let changed = false;
      for (const b of prev.buildings) {
        if (b.selected && next[b.id] === undefined) {
          next[b.id] = [{ packageType: 'Portfolio', tier: 'Basic' }];
          changed = true;
        }
      }
      // Drop entries for buildings that were removed or unselected.
      const validIds = new Set(prev.buildings.filter((b) => b.selected).map((b) => b.id));
      for (const id of Object.keys(next)) {
        if (!validIds.has(id)) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? { ...prev, packagesByBuildingId: next } : prev;
    });
  }, [state.buildings]);

  const goNext = useCallback(() => {
    setState((prev) => ({ ...prev, currentStep: Math.min(prev.currentStep + 1, STEP_FERDIG) }));
  }, []);

  const goBack = useCallback(() => {
    // On the first step there is nothing to go back to, so "Tilbake" exits the wizard
    // and returns to the building list — mirroring the close (×) icon in the layout.
    if (state.currentStep === STEP_BYGG) {
      navigate(PortfolioPaths.BuildingList);
      return;
    }
    setState((prev) => ({ ...prev, currentStep: Math.max(prev.currentStep - 1, STEP_BYGG) }));
  }, [state.currentStep, navigate]);

  const setBuildings = useCallback((buildings: OnboardingBuilding[]) => {
    setState((prev) => ({ ...prev, buildings }));
  }, []);

  const setImportCompanies = useCallback((importCompanies: ImportCompanyCandidate[]) => {
    setState((prev) => ({ ...prev, importCompanies }));
  }, []);

  const setPackagesByBuildingId = useCallback(
    (packagesByBuildingId: BuildingWizardState['packagesByBuildingId']) => {
      setState((prev) => ({ ...prev, packagesByBuildingId }));
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    setSubmitError(null);

    const selectedBuildings = state.buildings.filter((b) => b.selected);
    const selectedImports = state.importCompanies.filter((c) => c.selected);

    const buildings: BulkBuildingPayload[] = selectedBuildings.map((b) => {
      const childBuildings: BulkChildBuildingPayload[] | null =
        b.isGrouped && b.childBuildings
          ? b.childBuildings.map((c) => ({
              buildingNumber: c.buildingNumber ?? null,
              bra: c.bra,
              numberOfFloors: c.numberOfFloors ?? null,
              matrikkelBuildingTypeCode: c.matrikkelBuildingTypeCode ?? null,
              energyLabel: c.energyLabel ?? null,
              heatingGrade: c.heatingGrade ?? null,
              constructionYear: c.constructionYear ?? null,
              energyCertificateUrl: c.energyCertificateUrl ?? null,
              energyCertificateNumber: c.energyCertificateNumber ?? null,
              energyCertificateIssuedDate: c.energyCertificateIssuedDate ?? null,
            }))
          : null;

      const packages: BulkBuildingPackagePayload[] = (state.packagesByBuildingId[b.id] ?? []).map(
        (p) => ({ packageType: p.packageType, tier: p.tier }),
      );

      return {
        name: b.name ?? b.address,
        address: b.address,
        postalCode: b.postalCode,
        city: b.city,
        countryThreeLetterCode: b.countryThreeLetterCode ?? null,
        streetName: b.streetName ?? null,
        streetNumber: b.streetNumber ?? null,
        source: b.source ?? null,
        type: b.type,
        bra: b.bra,
        role: b.role,
        numberOfFloors: b.numberOfFloors ?? null,
        buildingNumber: b.buildingNumber ?? null,
        energySourceType: b.energySourceType ?? null,
        heatingType: b.heatingType ?? null,
        matrikkelBuildingTypeCode: b.matrikkelBuildingTypeCode ?? null,
        latitude: b.latitude ?? null,
        longitude: b.longitude ?? null,
        gnr: b.gnr ?? null,
        bnr: b.bnr ?? null,
        municipalityCode: b.municipalityCode ?? null,
        ownerOrgNumber: b.ownerOrgNumber ?? null,
        ownerName: b.ownerName ?? null,
        owners: b.owners?.map((o) => ({
          name: o.name ?? null,
          organizationNumber: o.organizationNumber ?? null,
          isJuridical: o.isJuridical,
          shareNumerator: o.shareNumerator ?? null,
          shareDenominator: o.shareDenominator ?? null,
        })) ?? null,
        ownerPath: b.ownerPath ?? null,
        ownerPathRootOrgNumber: b.ownerPathRootOrgNumber ?? null,
        energyLabel: b.energyLabel ?? null,
        heatingGrade: b.heatingGrade ?? null,
        constructionYear: b.constructionYear ?? null,
        energyCertificateUrl: b.energyCertificateUrl ?? null,
        energyCertificateNumber: b.energyCertificateNumber ?? null,
        energyCertificateIssuedDate: b.energyCertificateIssuedDate ?? null,
        isGrouped: b.isGrouped ?? false,
        childBuildings,
        packages,
      };
    });

    // Same FE -> BE contract as portfolio onboarding: send only { orgNr, name }
    // and let the BE re-fetch the canonical BRREG snapshot.
    const importCompanies: BulkImportCompanyPayload[] = selectedImports.map((c) => ({
      orgNr: c.orgNumber,
      name: c.name,
    }));

    try {
      const res = await createBuildings({
        body: { buildings, importCompanies: importCompanies.length > 0 ? importCompanies : null },
      }).unwrap();

      setCreatedBuildingIds(res.buildingIds);
      window.localStorage.removeItem(STORAGE_KEY);
      setState((prev) => ({ ...prev, currentStep: STEP_FERDIG }));
    } catch (err: any) {
      setSubmitError(err?.data?.detail ?? err?.message ?? 'En feil oppstod. Prøv igjen.');
    }
  }, [state, createBuildings]);

  const renderStep = () => {
    switch (state.currentStep) {
      case STEP_BYGG:
        return (
          <StepBygg
            company={null}
            buildings={state.buildings}
            onBuildingsChange={setBuildings}
            onBack={goBack}
            onNext={goNext}
          />
        );
      case STEP_ROLLER:
        return (
          <StepRoller
            buildings={state.buildings}
            onBuildingsChange={setBuildings}
            onBack={goBack}
            onNext={goNext}
          />
        );
      case STEP_IMPORTER:
        return (
          <StepImporter
            buildings={state.buildings}
            importCompanies={state.importCompanies}
            selfOrgNr={null}
            onImportCompaniesChange={setImportCompanies}
            onBack={goBack}
            onNext={goNext}
          />
        );
      case STEP_PAKKER:
        return (
          <StepPakker
            buildings={state.buildings}
            packagesByBuildingId={state.packagesByBuildingId}
            onPackagesChange={setPackagesByBuildingId}
            onBack={goBack}
            onNext={goNext}
          />
        );
      case STEP_GJENNOMGANG:
        return (
          <StepGjennomgang
            buildings={state.buildings}
            importCompanies={state.importCompanies}
            packagesByBuildingId={state.packagesByBuildingId}
            onBack={goBack}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />
        );
      case STEP_FERDIG:
        return (
          <StepFerdig buildings={state.buildings} createdBuildingIds={createdBuildingIds} />
        );
      default:
        return null;
    }
  };

  return (
    <BuildingWizardLayout currentStep={state.currentStep}>{renderStep()}</BuildingWizardLayout>
  );
}
